import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { plotOfficerService } from '../../services/plotOfficerService';
import {
  FileText,
  MapPin,
  Calendar,
  Plus,
  Search,
  Filter,
  Eye,
  Save,
  Users,
  Sprout,
  Eye as EyeIcon,
  BarChart3,
  Clock,
  CheckCircle,
  AlertCircle,
  Play,
  List,
  Grid,
  Camera,
  Upload,
  X
} from 'lucide-react';

const DataEntry = () => {
  const { user } = useAuth();
  const [assignedTrials, setAssignedTrials] = useState([]);

  // Load assigned trials from API with fallback to localStorage
  const loadAssignedTrials = async () => {
    try {
      const response = await plotOfficerService.getAssignedTrials();
      if (response.success) {
        setAssignedTrials(response.data || response);
      } else {
        throw new Error(response.message || 'Failed to load assigned trials');
      }
    } catch (error) {
      console.warn('API failed, falling back to localStorage:', error.message);
      // Fallback to localStorage
    try {
      const rawT = window.localStorage.getItem('trials');
      const rawA = window.localStorage.getItem('trialAssignments');
      const trials = rawT ? JSON.parse(rawT) : [];
      const assigns = (rawA ? JSON.parse(rawA) : []).filter(a => (a.assignedOfficer || '').toLowerCase() === (user?.name || '').toLowerCase());
      const byTrial = assigns.reduce((acc, a) => {
        const t = trials.find(tt => tt.id === a.trialId) || {};
        if (!acc[a.trialId]) {
          acc[a.trialId] = {
            id: a.trialId,
            trialCode: (a.locationCode || '').slice(0, -3),
            trialName: t.name || '-',
            crop: t.crop || '-',
            season: t.season || '-',
            year: t.year || '-',
            status: t.status || 'active',
            locations: []
          };
        }
        acc[a.trialId].locations.push({
          id: a.id,
          locationCode: a.locationCode,
          locationName: a.locationName,
          culture: a.culture,
          treatment: a.treatment,
          replications: a.replications,
          observations: a.observations || [],
          lastEntryDate: a.lastEntryDate || '-',
          startDate: a.assignedDate || '-',
          status: a.status || 'active',
          pendingEntries: 0
        });
        return acc;
      }, {});
        setAssignedTrials(Object.values(byTrial));
      } catch {
        setAssignedTrials([]);
      }
    }
  };

  useEffect(() => {
    loadAssignedTrials();
  }, [user]);

  const [selectedTrial, setSelectedTrial] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showDataEntryModal, setShowDataEntryModal] = useState(false);
  const [showLocationDetailsModal, setShowLocationDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [entriesByLocation, setEntriesByLocation] = useState({});
  const [editingEntryId, setEditingEntryId] = useState(null);

  const { register, handleSubmit, reset, formState: { errors }, setValue, getValues } = useForm();

  // Photo upload functions
  const handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    const newPhotos = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: URL.createObjectURL(file)
    }));
    setUploadedPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (photoId) => {
    setUploadedPhotos(prev => {
      const photoToRemove = prev.find(p => p.id === photoId);
      if (photoToRemove) {
        URL.revokeObjectURL(photoToRemove.preview);
      }
      return prev.filter(p => p.id !== photoId);
    });
  };

  const openPhotoPreview = (photo) => {
    setPhotoPreview(photo);
  };

  const closePhotoPreview = () => {
    setPhotoPreview(null);
  };

  const observations = [
    'Germination %',
    'Germination Vigour',
    'Population',
    'Vigour',
    'Germination DAS',
    'Plant height',
    'Plant width',
    'No. of branches',
    'Yield'
  ];

  // Cultures available are driven by the assignment (admin/scientist)
  const availableCultures = selectedLocation?.cultures || (selectedLocation?.culture ? [selectedLocation.culture] : []);

  const summarizeReplication = (replicationData = []) => {
    return (replicationData || [])
      .map((rep, idx) => `R${idx + 1}: ${rep?.value ?? '-'}`)
      .join(', ');
  };

  const handleQuickSaveReplication = () => {
    const formValues = getValues();
    const payload = {
      culture: formValues.culture,
      observation: formValues.observation,
      replicationData: formValues.replicationData
    };

    if (!payload.culture || !payload.observation) {
      toast.error('Select culture and observation first');
      return;
    }

    setEntriesByLocation(prev => {
      const key = `${selectedLocation.id}`;
      const current = prev[key] || [];
      if (editingEntryId) {
        // update existing
        const updated = current.map(e => e.id === editingEntryId ? {
          ...e,
          culture: payload.culture,
          observation: payload.observation,
          replicationData: payload.replicationData
        } : e);
        return { ...prev, [key]: updated };
      }
      // add new
      return {
        ...prev,
        [key]: [
          ...current,
          {
            id: Date.now(),
            culture: payload.culture,
            observation: payload.observation,
            replicationData: payload.replicationData
          }
        ]
      };
    });

    // clear replication values only
    const cleared = Array.from({ length: selectedLocation.replications }, () => ({ value: '' }));
    setValue('replicationData', cleared, { shouldValidate: false });
    if (editingEntryId) {
      setEditingEntryId(null);
      toast.success('Entry updated');
    } else {
      toast.success('Entry added');
    }
  };

  const handleEditEntry = (entry) => {
    setValue('culture', entry.culture);
    setValue('observation', entry.observation);
    // Ensure replication array length matches
    const desired = selectedLocation.replications;
    let rep = entry.replicationData || [];
    if (rep.length < desired) {
      rep = [...rep, ...Array.from({ length: desired - rep.length }, () => ({ value: '' }))];
    } else if (rep.length > desired) {
      rep = rep.slice(0, desired);
    }
    setValue('replicationData', rep, { shouldValidate: false });
    setEditingEntryId(entry.id);
  };

  // Calculate data entry statistics
  const dataEntryStats = {
    totalLocations: assignedTrials.reduce((sum, trial) => sum + trial.locations.length, 0),
    activeLocations: assignedTrials.reduce((sum, trial) => 
      sum + trial.locations.filter(loc => loc.status === 'active').length, 0),
    completedToday: 5,
    overdueEntries: 2
  };

  const [activeTabTrialId, setActiveTabTrialId] = useState(null);

  const filteredTrials = assignedTrials.filter(trial => {
    const matchesSearch = trial.trialName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trial.trialCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trial.crop.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || trial.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleDataEntry = async (data) => {
    try {
      setIsLoading(true);
      
       const payload = {
         trialId: selectedTrial.id,
         locationId: selectedLocation.id,
         entryDate: data.entryDate,
         culture: data.culture,
         observation: data.observation,
         replicationData: data.replicationData,
         photos: uploadedPhotos.map(photo => ({
           name: photo.name,
           size: photo.size,
           type: photo.type
        }))
        };

      const response = await plotOfficerService.submitDataEntry(payload);
      if (response.success) {
       // store for local table display
       setEntriesByLocation(prev => {
         const key = `${selectedLocation.id}`;
         const current = prev[key] || [];
         return {
           ...prev,
           [key]: [
             ...current,
             {
               id: Date.now(),
               culture: payload.culture,
               observation: payload.observation,
               replicationData: payload.replicationData
             }
           ]
         };
       });

      toast.success('Data entry saved successfully!');
      // Keep modal open and clear only replication fields for next entry
      const preserved = {
        entryDate: data.entryDate,
        culture: data.culture,
        observation: data.observation,
        replicationData: Array.from({ length: selectedLocation.replications }, () => ({ value: '' }))
      };
      reset(preserved);
      setUploadedPhotos([]);
      } else {
        throw new Error(response.message || 'Failed to save data entry');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to save data entry');
    } finally {
      setIsLoading(false);
    }
  };

  const openDataEntryModal = (trial, location) => {
    setSelectedTrial(trial);
    setSelectedLocation(location);
    setValue('entryDate', new Date().toISOString().split('T')[0]);
    const firstCulture = (location.cultures && location.cultures[0]) || location.culture || '';
    setValue('culture', firstCulture);
    const firstObservation = (location.observations && location.observations[0]) || '';
    setValue('observation', firstObservation);
    setShowDataEntryModal(true);
  };

  const openLocationDetailsModal = (location) => {
    setSelectedLocation(location);
    setShowLocationDetailsModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeasonColor = (season) => {
    switch (season) {
      case 'Kharif':
        return 'bg-green-100 text-green-600';
      case 'Rabi':
        return 'bg-blue-100 text-blue-600';
      case 'Winter':
        return 'bg-gray-100 text-gray-600';
      case 'Summer':
        return 'bg-orange-100 text-orange-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Removed pending badge; statuses are only 'active' and 'completed'

  return (
    <div className="space-y-3 md:space-y-6 overflow-x-hidden px-3 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Data Entry Management</h1>
          <p className="text-gray-600">Manage your trial data entry tasks and observations</p>
        </div>
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <BarChart3 className="w-5 h-5 text-primary-600" />
          <span className="text-sm text-gray-600">Data Entry Officer</span>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-6">
        {/* Total Locations */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Locations</p>
              <p className="text-3xl font-bold text-gray-900">{dataEntryStats.totalLocations}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Active Locations */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Locations</p>
              <p className="text-3xl font-bold text-gray-900">{dataEntryStats.activeLocations}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Completed Today */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed Today</p>
              <p className="text-3xl font-bold text-gray-900">{dataEntryStats.completedToday}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Overdue Entries */}
        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Overdue</p>
              <p className="text-3xl font-bold text-gray-900">{dataEntryStats.overdueEntries}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search trials or locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          {/* View mode toggle removed as requested */}
        </div>
      </div>

      {/* Data Entry Tasks */}
      <div className="card p-3 md:p-6">
        <div className="mb-2">
          <h2 className="text-lg font-semibold text-gray-900">Data Entry Tasks</h2>
        </div>
        {filteredTrials.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap mb-4 pb-1">
            {filteredTrials.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTabTrialId(t.id)}
                className={`px-3 py-1 rounded-full text-xs sm:text-sm border shrink-0 ${activeTabTrialId === t.id || (!activeTabTrialId && filteredTrials[0].id === t.id) ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300'}`}
              >
                {t.trialName}
              </button>
            ))}
          </div>
        )}
        
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTrials.map((trial) => (
              <div key={trial.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{trial.trialName}</h3>
                    <p className="text-sm text-gray-600 font-mono">{trial.trialCode}</p>
                    <div className="flex items-center space-x-2 mt-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeasonColor(trial.season)}`}>
                        {trial.season}
                      </span>
                      <span className="text-sm text-gray-600">{trial.crop}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(trial.status)}`}>
                    {trial.status}
                  </span>
                </div>

                <div className="space-y-3">
                  <h4 className="text-md font-medium text-gray-700">Locations ({trial.locations.length})</h4>
                  {trial.locations.map((location) => (
                    <div key={location.id} className="border border-gray-200 rounded-lg p-3 overflow-hidden">
                      <div className="flex flex-wrap items-start justify-between mb-2 gap-2">
                        <div className="min-w-0">
                          <h5 className="font-medium text-gray-900 text-sm">{location.locationName}</h5>
                          <p className="text-xs text-gray-600 font-mono">{location.locationCode}</p>
                        </div>
                        {/* Pending badge moved near Details button below */}
                      </div>
                      <div className="space-y-1 text-xs text-gray-600">
                        <div className="flex items-center">
                          <Sprout className="w-3 h-3 mr-1" />
                          <span>{location.culture}</span>
                        </div>
                        <div className="flex items-center">
                          <Users className="w-3 h-3 mr-1" />
                          <span>Reps: {location.replications}</span>
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          <span>Next: {location.nextEntryDate}</span>
                        </div>
                      </div>
                       <div className="flex flex-wrap gap-2 mt-3 justify-end items-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full shrink-0 whitespace-nowrap ${getStatusColor(location.status)}`}>
                          {location.status}
                        </span>
                        <button
                          onClick={() => openLocationDetailsModal(location)}
                          className="text-blue-600 hover:text-blue-800 text-xs inline-flex items-center"
                        >
                          <EyeIcon className="w-3 h-3 inline mr-1" />
                          Details
                        </button>
                        <button
                          onClick={() => openDataEntryModal(trial, location)}
                          className="text-green-600 hover:text-green-800 text-xs inline-flex items-center"
                        >
                          <Plus className="w-3 h-3 inline mr-1" />
                          Enter Data
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTrials
              .filter(t => (activeTabTrialId ? t.id === activeTabTrialId : t.id === (filteredTrials[0]?.id)))
              .map((trial) => (
              <div key={trial.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{trial.trialName}</h3>
                    <p className="text-sm text-gray-600 font-mono">{trial.trialCode}</p>
                    <div className="flex items-center space-x-2 mt-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeasonColor(trial.season)}`}>
                        {trial.season}
                      </span>
                      <span className="text-sm text-gray-600">{trial.crop}</span>
                      <span className="text-sm text-gray-600">{trial.year}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(trial.status)}`}>
                    {trial.status}
                  </span>
                </div>

                <div className="space-y-3">
                  <h4 className="text-md font-medium text-gray-700">Assigned Locations ({trial.locations.length})</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {trial.locations.map((location) => (
                      <div key={location.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h5 className="font-medium text-gray-900">{location.locationName}</h5>
                            <p className="text-sm text-gray-600 font-mono">{location.locationCode}</p>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex items-center">
                            <Sprout className="w-4 h-4 mr-2" />
                            <span>{location.culture} - {location.treatment}</span>
                          </div>
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-2" />
                            <span>Replications: {location.replications}</span>
                          </div>
                          
                         <div className="flex justify-end space-x-2 pt-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(location.status)}`}>
                            {location.status}
                          </span>
                          <button
                            onClick={() => openLocationDetailsModal(location)}
                            className="text-blue-600 hover:text-blue-800 text-xs inline-flex items-center"
                          >
                            <EyeIcon className="w-3 h-3 mr-1" /> Details
                          </button>
                          <button
                            onClick={() => openDataEntryModal(trial, location)}
                            className="text-green-600 hover:text-green-800 text-xs inline-flex items-center"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Enter Data
                          </button>
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredTrials.length === 0 && (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No data entry tasks found.</p>
          </div>
        )}
      </div>

      {/* Data Entry Modal */}
      {showDataEntryModal && selectedTrial && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Data Entry: {selectedLocation.locationName}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Trial: {selectedTrial.trialName} ({selectedTrial.trialCode})
              </p>
              
              <form onSubmit={handleSubmit(handleDataEntry)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entry Date</label>
                  <input
                    type="date"
                    {...register('entryDate', { required: 'Entry date is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    readOnly
                  />
                  {errors.entryDate && <p className="text-red-500 text-xs mt-1">{errors.entryDate.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Culture</label>
                  <select
                    {...register('culture', { required: 'Culture is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select Culture</option>
                    {availableCultures.map(culture => (
                      <option key={culture} value={culture}>{culture}</option>
                    ))}
                  </select>
                  {errors.culture && <p className="text-red-500 text-xs mt-1">{errors.culture.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observation</label>
                  <select
                    {...register('observation', { required: 'Observation is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select Observation</option>
                    {selectedLocation.observations.map(obs => (
                      <option key={obs} value={obs}>{obs}</option>
                    ))}
                  </select>
                  {errors.observation && <p className="text-red-500 text-xs mt-1">{errors.observation.message}</p>}
                </div>

                {/* Feedback moved to bottom as per requirement */}

                {/* Individual Location Trial Details removed as per requirement */}

                {/* Replication Data Entry */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Replication Data (Based on {selectedLocation.replications} replications)
                  </label>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">R1</label>
                      <input
                        type="number"
                        step="0.01"
                        {...register(`replicationData.0.value`, { 
                          required: 'Value for R1 is required',
                          min: { value: 0, message: 'Value must be positive' }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        placeholder="Enter value for R1"
                      />
                      {errors.replicationData?.[0]?.value && (
                        <p className="text-red-500 text-xs mt-1">{errors.replicationData[0].value.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">R2</label>
                      <input
                        type="number"
                        step="0.01"
                        {...register(`replicationData.1.value`, { 
                          required: 'Value for R2 is required',
                          min: { value: 0, message: 'Value must be positive' }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        placeholder="Enter value for R2"
                      />
                      {errors.replicationData?.[1]?.value && (
                        <p className="text-red-500 text-xs mt-1">{errors.replicationData[1].value.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">R3</label>
                      <input
                        type="number"
                        step="0.01"
                        {...register(`replicationData.2.value`, { 
                          required: 'Value for R3 is required',
                          min: { value: 0, message: 'Value must be positive' }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        placeholder="Enter value for R3"
                      />
                      {errors.replicationData?.[2]?.value && (
                        <p className="text-red-500 text-xs mt-1">{errors.replicationData[2].value.message}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button type="button" onClick={handleQuickSaveReplication} className="btn-primary">
                      Save Replication Entry
                    </button>
                  </div>
                </div>

                {/* Saved entries table moved above photo upload */}
                {entriesByLocation[String(selectedLocation.id)]?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Saved Entries</label>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Culture</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Observation</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Replication Data</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {entriesByLocation[String(selectedLocation.id)].map((e) => (
                            <tr key={e.id}>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{e.culture}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{e.observation}</td>
                              <td className="px-4 py-2 whitespace-normal text-sm text-gray-700 break-words">{summarizeReplication(e.replicationData)}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-right">
                                <button type="button" className="text-primary-600 hover:underline" onClick={() => handleEditEntry(e)}>
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                  </div>
                </div>
                )}

                {/* Photo Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Field Photos & Documentation</label>
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                      <input type="file" multiple accept="image/*" onChange={handlePhotoUpload} className="hidden" id="photo-upload" />
                      <label htmlFor="photo-upload" className="cursor-pointer">
                        <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-1">Click to upload photos or drag and drop</p>
                        <p className="text-xs text-gray-500">Supports JPG, PNG, GIF (Max 5MB each)</p>
                      </label>
                    </div>
                    {uploadedPhotos.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Uploaded Photos ({uploadedPhotos.length})</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {uploadedPhotos.map((photo) => (
                            <div key={photo.id} className="relative group">
                              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                                <img src={photo.preview} alt={photo.name} className="w-full h-full object-cover cursor-pointer" onClick={() => openPhotoPreview(photo)} />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                                  <button onClick={() => removePhoto(photo.id)} className="opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-all duration-200">
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              <p className="text-xs text-gray-600 mt-1 truncate">{photo.name}</p>
                              <p className="text-xs text-gray-500">{(photo.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Farmer Feedback Section (moved to last) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Farmer Feedback & Observations
                  </label>
                  <div>
                    <textarea
                      {...register('farmerFeedback')}
                      rows="4"
                      placeholder="Enter farmer's feedback, observations, concerns, or recommendations..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
                    />
                  </div>
                </div>

                {/* Plot Officer Feedback Section (moved to last) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Plot Officer Technical Assessment
                  </label>
                  <div>
                    <textarea
                      {...register('officerFeedback')}
                      rows="4"
                      placeholder="Enter technical observations, issues encountered, recommendations, or follow-up actions..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDataEntryModal(false);
                      reset();
                      setSelectedLocation(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? 'Saving...' : 'Save Data Entry'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Location Details Modal */}
      {showLocationDetailsModal && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Location Details: {selectedLocation.locationName}
              </h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Location Code</p>
                    <p className="text-sm text-gray-900 font-mono">{selectedLocation.locationCode}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Culture & Treatment</p>
                    <p className="text-sm text-gray-900">{selectedLocation.culture} - {selectedLocation.treatment}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Number of Replications</p>
                    <p className="text-sm text-gray-900">{selectedLocation.replications}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Pending Entries</p>
                    <p className="text-sm text-gray-900">{selectedLocation.pendingEntries}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Last Entry Date</p>
                    <p className="text-sm text-gray-900">{selectedLocation.lastEntryDate}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Start Date</p>
                    <p className="text-sm text-gray-900">{selectedLocation.startDate}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Observations to Record</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedLocation.observations.map((obs, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {obs}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowLocationDetailsModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Preview Modal */}
      {photoPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-[90vh] overflow-hidden">
            <button
              onClick={closePhotoPreview}
              className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75 transition-all"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={photoPreview.preview}
              alt={photoPreview.name}
              className="max-w-full max-h-full object-contain"
            />
            <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded-lg">
              <p className="text-sm font-medium">{photoPreview.name}</p>
              <p className="text-xs">
                {(photoPreview.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataEntry; 